import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import ThemeProvider from './theme/ThemeProvider';
import ErrorBoundary from './components/ErrorBoundary';

const APP_SHELL_VERSION = '2026-04-26-emergency-confirm-v1';
const APP_SHELL_VERSION_KEY = 'rvAppShellVersion';
const APP_SHELL_MIGRATION_FLAG = 'rvAppShellMigrationInProgress';

const runAppShellMigration = async () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const currentVersion = window.localStorage.getItem(APP_SHELL_VERSION_KEY);
  if (currentVersion === APP_SHELL_VERSION) {
    return false;
  }

  if (window.sessionStorage.getItem(APP_SHELL_MIGRATION_FLAG) === '1') {
    window.localStorage.setItem(APP_SHELL_VERSION_KEY, APP_SHELL_VERSION);
    window.sessionStorage.removeItem(APP_SHELL_MIGRATION_FLAG);
    return false;
  }

  window.sessionStorage.setItem(APP_SHELL_MIGRATION_FLAG, '1');
  window.localStorage.setItem(APP_SHELL_VERSION_KEY, APP_SHELL_VERSION);

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }

    if ('caches' in window) {
      const cacheKeys = await window.caches.keys();
      await Promise.all(cacheKeys.map((key) => window.caches.delete(key)));
    }
  } catch (error) {
    console.warn('App shell migration failed:', error);
  }

  window.location.reload();
  return true;
};

// Prevent pinch zoom on mobile devices
document.addEventListener('gesturestart', (e) => e.preventDefault());
document.addEventListener('gesturechange', (e) => e.preventDefault());
document.addEventListener('gestureend', (e) => e.preventDefault());

// Prevent double-tap zoom
let lastTouchEnd = 0;
document.addEventListener('touchend', (e) => {
  const now = Date.now();
  if (now - lastTouchEnd <= 300) {
    e.preventDefault();
  }
  lastTouchEnd = now;
}, { passive: false });

// Prevent ctrl+wheel zoom (desktop) and pinch zoom
document.addEventListener('wheel', (e) => {
  if (e.ctrlKey) {
    e.preventDefault();
  }
}, { passive: false });

runAppShellMigration().then((didReload) => {
  if (didReload) {
    return;
  }

  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
});

// Register Service Worker for offline support
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('Service Worker registered:', registration.scope);
        
        // Store registration globally for background sync
        window.swRegistration = registration;

        // Proactively check for updates on load
        registration.update();

        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          console.log('Service Worker update found');
          
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // Activate new worker immediately
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch((error) => {
        console.error('Service Worker registration failed:', error);
      });

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'SYNC_STATUS') {
        console.log('Sync status from SW:', event.data);
        // Dispatch custom event for components to listen to
        window.dispatchEvent(new CustomEvent('sw-sync-status', { detail: event.data }));
      }
    });
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
